import React from "react";
import PropTypes from "prop-types";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import { withStyles } from "@material-ui/styles";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Avatar from "@material-ui/core/Avatar";
import CardGiftcardOutlinedIcon from "@material-ui/icons/CardGiftcardOutlined";
import { CardHeader, Divider, Chip, Box } from "@material-ui/core";
import ViewListIcon from "@material-ui/icons/ViewList";
import CircularProgress from "@material-ui/core/CircularProgress";
import WarningIcon from "@material-ui/icons/Warning";

const useStyles = () => ({
  card: {
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    borderRadius: 12,
    backgroundColor: "#fafafa",
  },
  title: {
    color: "#3f51b5",
    display: "flex",
    alignItems: "center",
    fontSize: 20,
    fontWeight: 600,
  },
  titleIcon: {
    marginRight: 12,
    fontSize: 28,
  },
  titleDivider: {
    backgroundColor: "#e0e0e0",
    marginBottom: 8,
  },
  cardContent: {
    padding: "0 8px",
    overflow: "auto",
    backgroundColor: "#fafafa",
  },
  listItem: {
    outline: "none",
    cursor: "pointer",
    padding: "12px 8px",
    marginBottom: 8,
    borderRadius: 8,
    backgroundColor: "#ffffff",
    border: "1px solid #e0e0e0",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f5f5f5",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    },
    "&$selected": {
      backgroundColor: "#e8eaf6",
      borderColor: "#3f51b5",
      borderWidth: 2,
    },
  },
  listItemDisputed: {
    outline: "none",
    cursor: "pointer",
    padding: "12px 8px",
    marginBottom: 8,
    borderRadius: 8,
    backgroundColor: "#fff3e0",
    border: "2px solid #ff9800",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#ffe0b2",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(255, 152, 0, 0.2)",
    },
  },
  selected: {},
  circularProgressStyle: {
    textAlign: "center",
    marginTop: 20,
    marginBottom: 20,
  },
  primaryTextStyle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#333",
    marginBottom: 4,
  },
  secondaryTextStyle: {
    fontSize: 13,
    color: "#666",
    lineHeight: 1.5,
  },
  orderSubTitle: {
    fontSize: 16,
    fontWeight: 700,
    width: "100%",
    color: "#3f51b5",
    textAlign: "left",
    padding: "12px 0 8px 0",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  jobItem: {
    padding: "4px 0",
    display: "flex",
    alignItems: "center",
  },
  jobDivider: {
    margin: "8px 0",
    backgroundColor: "#e0e0e0",
  },
  statusSection: {
    marginTop: 8,
    paddingTop: 8,
    borderTop: "1px solid #e0e0e0",
  },
  disputedChip: {
    backgroundColor: "#ff9800",
    color: "#fff",
    fontSize: 11,
    height: 20,
    marginLeft: 8,
  },
  avatar: {
    backgroundColor: "#3f51b5",
  },
  avatarDisputed: {
    backgroundColor: "#ff9800",
  },
  orderHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  jobsSection: {
    marginTop: 8,
    backgroundColor: "#f9f9f9",
    padding: 8,
    borderRadius: 4,
  },
  jobLabel: {
    fontWeight: 600,
    fontSize: 13,
    color: "#3f51b5",
    marginBottom: 4,
  },
});

class OrderTab extends React.Component {
  render() {
    const { classes } = this.props;
    const minWidthValue = (18.94 / 100) * window.innerWidth;
    
    return (
      <React.Fragment>
        <Card
          style={{
            minWidth: minWidthValue,
            maxWidth: minWidthValue,
            minHeight: window.innerHeight - 120,
            maxHeight: window.innerHeight - 120,
          }}
          className={classes.card}
        >
          <CardHeader
            style={{ padding: "16px" }}
            title={
              <div className={classes.title}>
                <ViewListIcon className={classes.titleIcon} />
                <span>
                  Orders ({this.props.values.dispatchOrders?.length || 0})
                </span>
              </div>
            }
          />
          <Divider className={classes.titleDivider} />
          
          <CardContent className={classes.cardContent}>
            {this.props.values.ordersDataLoading ? (
              <div className={classes.circularProgressStyle}>
                <CircularProgress />
              </div>
            ) : (
              <List>
                {/* Ready To Dispatch Section */}
                <ListItem>
                  <Typography className={classes.orderSubTitle}>
                    Ready To Dispatch
                  </Typography>
                </ListItem>

                {this.props.values.dispatchOrders?.map((card) => (
                  <ListItem
                    key={card.orderId}
                    className={
                      card.isDisputed === 1
                        ? classes.listItemDisputed
                        : classes.listItem
                    }
                    classes={{ selected: classes.selected }}
                    onClick={() => this.props.getOrderId(card)}
                    selected={
                      this.props.values.selectedOrder?.orderId === card.orderId
                    }
                  >
                    <ListItemAvatar style={{ minWidth: 50 }}>
                      <Avatar
                        className={
                          card.isDisputed === 1
                            ? classes.avatarDisputed
                            : classes.avatar
                        }
                      >
                        {card.isDisputed === 1 ? (
                          <WarningIcon />
                        ) : (
                          <CardGiftcardOutlinedIcon />
                        )}
                      </Avatar>
                    </ListItemAvatar>
                    
                    <ListItemText
                      disableTypography
                      primary={
                        <Box>
                          <div className={classes.orderHeader}>
                            <Typography className={classes.primaryTextStyle}>
                              Order #{card.orderId}
                            </Typography>
                            {card.isDisputed === 1 && (
                              <Chip
                                label="DISPUTED"
                                size="small"
                                className={classes.disputedChip}
                              />
                            )}
                          </div>
                          {/* <ListItemAvatar style={{ minWidth: 56 }}>
                      <Avatar
                        className={
                          card.isDisputed === 1
                            ? classes.avatarDisputed
                            : classes.avatar
                        }
                      >
                        {card.isDisputed === 1 ? (
                          <WarningIcon />
                        ) : (
                          <CardGiftcardOutlinedIcon />
                        )}
                      </Avatar>
                    </ListItemAvatar> */}
                          <Typography className={classes.secondaryTextStyle}>
                            Created by: {card.createdBy}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <div className={classes.jobsSection}>
                            <Typography className={classes.jobLabel}>
                              Jobs:
                            </Typography>
                            {card.jobDetails?.map((job, index) => (
                              <div key={index} className={classes.jobItem}>
                                <Typography className={classes.secondaryTextStyle}>
                                  • {job.itemId} - {job.productLabel}
                                </Typography>
                              </div>
                            ))}
                          </div>
                          
                          <div className={classes.statusSection}>
                            <Typography
                              className={classes.secondaryTextStyle}
                              style={{ fontSize: 12 }}
                            >
                              Last updated: {card.statusChangedOn}
                            </Typography>
                          </div>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}

                {/* In Queue Section */}
                <ListItem style={{ marginTop: 16 }}>
                  <Typography className={classes.orderSubTitle}>
                    In Queue
                  </Typography>
                </ListItem>

                {this.props.values.qaOrders?.map((card) => (
                  <ListItem
                    key={card.orderId}
                    className={classes.listItem}
                    classes={{ selected: classes.selected }}
                    onClick={() => this.props.getOrderId(card)}
                    selected={
                      this.props.values.selectedOrder?.orderId === card.orderId
                    }
                  >
                    <ListItemAvatar style={{ minWidth: 56 }}>
                      <Avatar className={classes.avatar}>
                        <CardGiftcardOutlinedIcon />
                      </Avatar>
                    </ListItemAvatar>
                    
                    <ListItemText
                      disableTypography
                      primary={
                        <Box>
                          <Typography className={classes.primaryTextStyle}>
                            Order #{card.orderId}
                          </Typography>
                          <Typography className={classes.secondaryTextStyle}>
                            Created by: {card.createdBy}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <div className={classes.jobsSection}>
                            <Typography className={classes.jobLabel}>
                              Jobs:
                            </Typography>
                            {card.jobDetails?.map((job, index) => (
                              <div key={index} className={classes.jobItem}>
                                <Typography className={classes.secondaryTextStyle}>
                                  {job.itemId} - {job.productLabel}
                                </Typography>
                              </div>
                            ))}
                          </div>
                          
                          <div className={classes.statusSection}>
                            <Typography
                              className={classes.secondaryTextStyle}
                              style={{ fontSize: 12 }}
                            >
                              Last updated: {card.statusChangedOn}
                            </Typography>
                          </div>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </React.Fragment>
    );
  }
}

OrderTab.propTypes = {
  classes: PropTypes.object.isRequired,
  values: PropTypes.object,
  getOrderId: PropTypes.func,
  onHandleChange: PropTypes.func,
};

OrderTab.defaultProps = {
  values: {},
  getOrderId: (fn) => fn,
  onHandleChange: (fn) => fn,
};

export default withStyles(useStyles)(OrderTab);
// /* eslint-disable eqeqeq */
// import React from "react";
// import PropTypes from "prop-types";
// import Card from "@material-ui/core/Card";
// import CardContent from "@material-ui/core/CardContent";
// import Typography from "@material-ui/core/Typography";
// import { withStyles } from "@material-ui/styles";
// import List from "@material-ui/core/List";
// import ListItem from "@material-ui/core/ListItem";
// import ListItemText from "@material-ui/core/ListItemText";
// import ListItemAvatar from "@material-ui/core/ListItemAvatar";
// import Avatar from "@material-ui/core/Avatar";
// import CardGiftcardOutlinedIcon from "@material-ui/icons/CardGiftcardOutlined";
// import { CardHeader, Divider } from "@material-ui/core";
// import ViewListIcon from "@material-ui/icons/ViewList";
// import CircularProgress from "@material-ui/core/CircularProgress";
// import { IsEmpty } from "../../../../utils/helper";

// const useStyles = () => ({
//   card: {
//     display: "flex",
//     flexDirection: "column",
//     boxShadow:
//       "0 4px 4px 0 rgba(0, 0, 0, 0.2), 0 2px 15px 0 rgba(0, 0, 0, 0.19)",
//     borderRadius: 10,
//     // height: "100%",
//   },
//   title: {
//     color: "#3f51b5",
//     display: "flex",
//     fontSize: 20,
//     marginTop: 5,
//   },
//   titleIcon: {
//     marginRight: 20,
//     fontSize: 28,
//   },
//   titleDivider: {
//     width: "95%",
//     backgroundColor: "#3f51b5",
//     opacity: "0.5",
//     marginLeft: 15,
//     marginBottom: 10,
//   },
//   cardContent: {
//     padding: 0,
//     width: "95%",
//     overflow: "auto",
//   },
//   listItem: {
//     outline: "none",
//     cursor: "pointer",
//     padding: 1,
//     paddingLeft: 2,
//   },
//   listItemDisputed: {
//     outline: "none",
//     cursor: "pointer",
//     padding: 1,
//     paddingLeft: 2,
//     // backgroundColor:"#fcae1e",
//     backgroundColor: "#ffe0a8",
//     color: "black",
//   },
//   statusLabel: {
//     fontWeight: 600,
//     marginRight: 7,
//     fontSize: 14,
//     width: "90%",
//     overflowWrap: "break-word",
//   },
//   resize: {
//     padding: 10,
//   },
//   circularProgressStyle: {
//     textAlign: "center",
//     marginTop: 10,
//   },
//   primaryTextStyle: {
//     fontSize: 15,
//     overflowWrap: "break-word",
//     width: "63%",
//   },
//   secondaryTextStyle: {
//     fontSize: 12,
//     color: "#787676",
//   },
//   orderSubTitle: {
//     fontSize: 18,
//     width: "100%",
//     color: "#3f51b5",
//     textAlign: "center",
//   },
// });

// class OrderTab extends React.Component {
//   render() {
//     const { classes } = this.props;
//     const minWidthValue = (18.94 / 100) * window.innerWidth;
//     return (
//       <React.Fragment>
//         <Card
//           style={{
//             minWidth: minWidthValue,
//             maxWidth: minWidthValue,
//             minHeight: window.innerHeight - 120,
//             maxHeight: window.innerHeight - 120,
//           }}
//           className={classes.card}
//         >
//           <CardHeader
//             style={{ padding: 10 }}
//             title={
//               <div
//                 style={{
//                   display: "flex",
//                   justifyContent: "space-between",
//                   marginRight: 15,
//                 }}
//               >
//                 <span className={classes.title}>
//                   <ViewListIcon className={classes.titleIcon} />
//                   <b>Orders ({this.props.values.dispatchOrders.length})</b>
//                 </span>
//               </div>
//             }
//           />
//           <Divider className={classes.titleDivider} />
//           <CardContent className={classes.cardContent}>
//             {this.props.values.ordersDataLoading ? (
//               <div className={classes.circularProgressStyle}>
//                 <CircularProgress />
//               </div>
//             ) : (
//               <List>
//                 <ListItem>
//                   <span className={classes.orderSubTitle}>
//                     <b>Ready To Dispatch</b>
//                   </span>
//                 </ListItem>

//                 {this.props.values.dispatchOrders.map((card) => {
//                   return (
//                     <ListItem
//                       key={card.orderId}
//                       className={
//                         card.isDisputed == 1
//                           ? classes.listItemDisputed
//                           : classes.listItem
//                       }
//                       onClick={() => this.props.getOrderId(card)}
//                       selected={
//                         this.props.values.selectedOrder.orderId === card.orderId
//                       }
//                     >
//                       <ListItemAvatar style={{ marginLeft: 10 }}>
//                         <Avatar>
//                           <CardGiftcardOutlinedIcon />
//                         </Avatar>
//                       </ListItemAvatar>
//                       <ListItemText
//                         disableTypography
//                         primary={
//                           <>
//                             <Typography className={classes.primaryTextStyle}>
//                               {" "}
//                             <b>Order #{card.orderId}</b> 
//                             </Typography>
//                             <Typography className={classes.primaryTextStyle}>
//                               {card.createdBy}{" "}
//                             </Typography>
//                             <br/>
//                           </>
//                         }
//                       secondary={
//                             <>
//                                <Typography className={classes.primaryTextStyle}>
//                                   <b>Jobs</b>
//                                 </Typography>
//                                   {card.jobDetails.map((job, index) => (
//                                     <div key={index}>
//                                       <Typography className={classes.secondaryTextStyle}>
//                                          <span>{job.itemId}</span>
//                                       - <span>{job.productLabel}</span> 
//                                       </Typography>
                                   
//                                     </div>
//                                   ))}

//                                 <Typography className={classes.secondaryTextStyle}>
//                                   last status changed on : {card.statusChangedOn}
//                                 </Typography>  

                                 
                              
//                             </>
//                           }
//                       />
//                     </ListItem>
//                   );
//                 })}

//                 <ListItem>
//                   <span className={classes.orderSubTitle}>
//                     <b>In Queue</b>
//                   </span>
//                 </ListItem>

//                 {this.props.values.qaOrders &&
//                   this.props.values.qaOrders.map((card) => {
//                     return (
//                       <ListItem
//                         key={card.orderId}
//                         className={classes.listItem}
//                         onClick={() => this.props.getOrderId(card)}
//                         selected={
//                           this.props.values.selectedOrder.orderId ===
//                           card.orderId
//                         }
//                       >
//                         <ListItemAvatar style={{ marginLeft: 10 }}>
//                           <Avatar>
//                             <CardGiftcardOutlinedIcon />
//                           </Avatar>
//                         </ListItemAvatar>
//                         <ListItemText
//                           disableTypography
//                            primary={
//                             <>
//                               <Typography className={classes.primaryTextStyle}>
//                                 {" "}
//                               <b>Order #{card.orderId}</b> 
//                               </Typography>
//                               <Typography className={classes.primaryTextStyle}>
//                                 {card.createdBy}{" "}
//                               </Typography>
//                               <br/>
//                             </>
//                           }
//                           secondary={
//                             <>
//                                <Typography className={classes.primaryTextStyle}>
//                                   <b>Jobs</b>
//                                 </Typography>
//                                   {card.jobDetails.map((job, index) => (
//                                     <div key={index}>
//                                       <Typography className={classes.secondaryTextStyle}>
//                                          <span>{job.itemId}</span>
//                                       - <span>{job.productLabel}</span> 
//                                       </Typography>
                                   
//                                     </div>
//                                   ))}

//                                 <Typography className={classes.primaryTextStyle}>
//                                   Last status changed on :
//                                 </Typography>  
//                                  <Typography className={classes.secondaryTextStyle}>
//                                   {card.statusChangedOn}
//                                 </Typography>  

                                 
                              
//                             </>
//                           }
//                           // secondary={
//                           //   <Typography className={classes.secondaryTextStyle}>
//                           //     {" "}
//                           //     {card.createdOn}{" "}
//                           //   </Typography>
//                           // }
//                         />
//                       </ListItem>
//                     );
//                   })}
//               </List>
//             )}
//           </CardContent>
//         </Card>
//       </React.Fragment>
//     );
//   }
// }

// OrderTab.propTypes = {
//   classes: PropTypes.object.isRequired,
//   values: PropTypes.object,
//   getOrderId: PropTypes.func,
//   onHandleChange: PropTypes.func,
// };

// OrderTab.defaultProps = {
//   values: {},
//   getOrderId: (fn) => fn,
//   onHandleChange: (fn) => fn,
// };

// export default withStyles(useStyles)(OrderTab);
